/**
 * Error Parsing Utilities
 * Extracts clean, user-friendly error messages from transaction errors
 */

const GENERIC_TX_FAILURE = 'This transaction cannot be completed. Check your inputs and try again.';
const GENERIC_UNEXPECTED = 'Something went wrong. Try again.';
const INSUFFICIENT_GAS_FUNDS =
    'Not enough funds for gas fees. Add funds to your wallet and try again.';
const UNKNOWN_ERROR = 'Something went wrong.';
const TX_CANCELLED = 'Transaction cancelled.';
const WALLET_ERROR = 'Wallet error. Try again.';
const WALLET_REQUEST_PENDING =
    'A wallet request is already open. Finish or dismiss it in your wallet, then try again.';
const WALLET_NETWORK_CHANGED = 'Your wallet network changed. Try again.';
const NETWORK_UNAVAILABLE = 'This network is not available in your wallet. Switch networks and try again.';
const PERMISSION_DENIED = 'Access denied. Wait for the page to finish connecting, then try again.';
const SERVICE_UNAVAILABLE = 'Service unavailable. Try again in a moment.';
const NETWORK_UNREACHABLE = 'Cannot reach the network. Try again.';
const INSUFFICIENT_TOKEN = 'Not enough tokens for this transaction.';
const INSUFFICIENT_ALLOWANCE = 'Token allowance too low. Approve more tokens first.';
const FEE_TOO_LOW = 'Network fees went up before the transaction was sent, so it did not go through. Try again.';

/**
 * Remove dash/em-dash punctuation from user-facing sentences.
 * Keeps hyphens inside words (e.g. URLs are unaffected; " - " mid-sentence is rewritten).
 * @param {string} message
 * @returns {string}
 */
function stripSentenceDashes(message) {
    if (!message || typeof message !== 'string') return message;
    return message
        .replace(/\s*[—–]\s*/g, '. ')
        .replace(/\s+-\s+/g, '. ')
        .replace(/\.\s*\./g, '.')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * True when the error (or a nested RPC error) means the account cannot pay for gas.
 * Exported so gas fallback logic can refuse to retry these as "estimation" failures.
 * @param {Error|Object|string} error
 * @returns {boolean}
 */
export function isInsufficientFundsError(error) {
    if (!error) return false;

    const code = error.code ?? error.error?.code;
    if (
        code === 'INSUFFICIENT_FUNDS' ||
        code === 'insufficient funds' ||
        String(code).toUpperCase() === 'INSUFFICIENT_FUNDS'
    ) {
        return true;
    }

    const parts = [
        typeof error === 'string' ? error : null,
        error.message,
        error.reason,
        error.shortMessage,
        error.info?.error?.message,
        error.error?.message,
        error.error?.reason,
        error.data?.message,
        error.error?.data?.message,
    ].filter((p) => typeof p === 'string' && p.length > 0);

    const combined = parts.join(' ');
    // Explicit insufficient-funds wording (not mere "insufficient gas" / estimation issues)
    if (/insufficient[_\s-]?funds/i.test(combined)) return true;
    if (/intrinsic transaction cost/i.test(combined)) return true;
    if (/gas \* price \+ value/i.test(combined)) return true;
    if (/exceeds the balance of the account/i.test(combined)) return true;
    // MetaMask / some RPCs when native balance is 0
    if (/gas required exceeds allowance\s*\(\s*0\s*\)/i.test(combined)) return true;
    return false;
}

/**
 * True when the network rejected the transaction because its max fee was
 * below the current base fee. Wallets report this through RPC wrappers
 * ("RPC … eth_sendRawTransaction: max fee per gas less than block base fee"),
 * which otherwise reads like a connection problem.
 * @param {Error|Object|string} error
 * @returns {boolean}
 */
export function isFeeTooLowError(error) {
    if (!error) return false;
    const parts = [
        typeof error === 'string' ? error : null,
        error.message,
        error.reason,
        error.shortMessage,
        error.info?.error?.message,
        error.error?.message,
        error.error?.reason,
        error.data?.message,
        error.error?.data?.message,
    ].filter((p) => typeof p === 'string' && p.length > 0);

    const combined = parts.join(' ');
    if (/less than (the )?block base fee/i.test(combined)) return true;
    if (/fee cap[^.]*less than/i.test(combined)) return true;
    // "replacement transaction underpriced" is a different problem (a pending tx).
    return /transaction underpriced/i.test(combined) && !/replacement transaction underpriced/i.test(combined);
}

/** Patterns that indicate ethers/viem/RPC/JS dumps that must never reach the UI. */
const TECHNICAL_LEAK_PATTERNS = [
    /\bCALL_EXCEPTION\b/i,
    /\bUNPREDICTABLE_GAS_LIMIT\b/i,
    /\bINSUFFICIENT_FUNDS\b/i,
    /\bmissing revert data\b/i,
    /\baction\s*=\s*["']?estimateGas/i,
    /\bcode\s*=\s*[A-Z_]+/i,
    /\bdata\s*=\s*null\b/i,
    /\breason\s*=\s*null\b/i,
    /\binvocation\s*=/i,
    /\brevert\s*=\s*null\b/i,
    /\btransaction\s*=\s*(?:,|\))/i,
    /\bethers@[\d.]+/i,
    /\bviem@[\d.]+/i,
    /\bwagmi@/i,
    /\bchrome-extension:\/\//i,
    /\bnode_modules\//i,
    /\bwebpack:\/\//i,
    /\binternal json-rpc\b/i,
    /\bjson[_ -]?rpc\b/i,
    /\beth_[a-z0-9_]+\b/i,
    /\bwallet_[a-z0-9_]+\b/i,
    /\bfirebaseerror\b/i,
    /\bfirestore\//i,
    /\bauth\//i,
    /\beconnrefused\b/i,
    /\beconnreset\b/i,
    /\betimedout\b/i,
    /\benotfound\b/i,
    /\bsyntaxerror\b/i,
    /\btypeerror\b/i,
    /\breferenceerror\b/i,
    /\bis not a function\b/i,
    /\bcannot read propert(?:y|ies)\b/i,
    /\bundefined is not (?:a function|an object)\b/i,
    /\bnull is not an object\b/i,
    /\b0x[a-f0-9]{40,}\b/i,
    /\bat\s+\S+\s+\(/i, // stack frame
];

/**
 * True when a message still contains library/RPC/JS internals that users should not see.
 * @param {string} message
 * @returns {boolean}
 */
function looksTechnical(message) {
    if (!message || typeof message !== 'string') return true;
    const trimmed = message.trim();
    if (!trimmed) return true;
    // Parenthetical dumps of key=value pairs from ethers
    if (/\(\s*(?:action|data|reason|transaction|invocation|revert|code)\s*=/i.test(trimmed)) {
        return true;
    }
    if (TECHNICAL_LEAK_PATTERNS.some((re) => re.test(trimmed))) {
        return true;
    }
    // Multi-line stack dumps
    if (/\n\s*at\s+/.test(trimmed)) {
        return true;
    }
    return false;
}

/**
 * Ensure the final message is safe for end users. Falls back if anything technical remains.
 * @param {string} message
 * @param {string} [fallback]
 * @returns {string}
 */
function ensureUserFriendly(message, fallback = GENERIC_TX_FAILURE) {
    if (!message || typeof message !== 'string') return fallback;
    const trimmed = stripSentenceDashes(message.trim());
    if (!trimmed || looksTechnical(trimmed)) return fallback;
    // Extremely long messages with no punctuation are almost always dumps
    if (trimmed.length > 280 && !/[.!?]/.test(trimmed)) return fallback;
    return trimmed;
}

/**
 * True when the user cancelled / rejected the wallet confirmation dialog.
 * @param {Error|Object|string} error
 * @returns {boolean}
 */
export function isUserRejectedError(error) {
    if (!error) return false;
    const code = error.code ?? error.error?.code;
    if (
        code === 'ACTION_REJECTED' ||
        code === 4001 ||
        code === '4001'
    ) {
        return true;
    }
    const parts = [
        typeof error === 'string' ? error : null,
        error.message,
        error.reason,
        error.error?.message,
        error.error?.reason,
    ].filter((p) => typeof p === 'string' && p.length > 0);
    const combined = parts.join(' ').toLowerCase();
    return (
        combined.includes('user rejected') ||
        combined.includes('user denied') ||
        combined.includes('user cancelled') ||
        combined.includes('user canceled')
    );
}

/**
 * Extracts a clean, user-friendly error message from an error object
 * @param {Error|Object} error - The error object from a transaction
 * @returns {string} - A clean, understandable error message
 */
export function getCleanErrorMessage(error) {
    try {
        if (!error) {
            return UNKNOWN_ERROR;
        }

        // Check for user cancellation first
        if (isUserRejectedError(error)) {
            return TX_CANCELLED;
        }

        // Insufficient native balance for gas — check before CALL_EXCEPTION /
        // UNPREDICTABLE_GAS_LIMIT wrappers that would otherwise hide this as a generic failure.
        if (isInsufficientFundsError(error)) {
            return INSUFFICIENT_GAS_FUNDS;
        }

        if (isFeeTooLowError(error)) {
            return FEE_TOO_LOW;
        }

        // ethers CALL_EXCEPTION / estimateGas failures with no usable revert reason
        const code = error.code || error.error?.code;
        const rawMessage = typeof error === 'string'
            ? error
            : (error.message || error.reason || error.error?.message || '');
        const lowerRaw = typeof rawMessage === 'string' ? rawMessage.toLowerCase() : '';
        if (
            code === 'CALL_EXCEPTION' ||
            code === 'UNPREDICTABLE_GAS_LIMIT' ||
            lowerRaw.includes('missing revert data') ||
            (lowerRaw.includes('call_exception') && !error.reason)
        ) {
            // Prefer a nested human reason when present; otherwise generic
            const nestedReason = error.reason || error.error?.reason;
            if (nestedReason && typeof nestedReason === 'string' && !looksTechnical(nestedReason)) {
                return ensureUserFriendly(formatErrorMessage(nestedReason));
            }
            return GENERIC_TX_FAILURE;
        }

        // Handle string errors
        if (typeof error === 'string') {
            return ensureUserFriendly(extractMessageFromString(error));
        }

        // Check for JavaScript runtime errors (technical errors that should be hidden from users)
        const errorMessage = error.message || error.toString() || '';
        if (typeof errorMessage === 'string') {
            const lowerMessage = errorMessage.toLowerCase();
            
            // Catch common JavaScript runtime errors and convert to user-friendly messages
            if (lowerMessage.includes('is not a function') ||
                lowerMessage.includes('cannot read property') ||
                lowerMessage.includes('cannot read properties') ||
                lowerMessage.includes('undefined is not an object') ||
                lowerMessage.includes('null is not an object') ||
                lowerMessage.includes('typeerror') ||
                lowerMessage.includes('referenceerror')) {
                // These are internal JavaScript errors - don't show technical details to users
                return GENERIC_UNEXPECTED;
            }
        }

        // Try to get the reason first (most specific) — skip if it is a technical dump
        if (error.reason && typeof error.reason === 'string' && !looksTechnical(error.reason)) {
            return ensureUserFriendly(formatErrorMessage(error.reason));
        }

        // Try nested error object (common in ethers.js)
        if (error.error) {
            const nestedError = error.error;
            if (nestedError.reason && !looksTechnical(nestedError.reason)) {
                return ensureUserFriendly(formatErrorMessage(nestedError.reason));
            }
            if (nestedError.message) {
                return ensureUserFriendly(extractMessageFromString(nestedError.message));
            }
            if (nestedError.data) {
                const dataMessage = extractMessageFromData(nestedError.data);
                if (dataMessage) return ensureUserFriendly(dataMessage);
            }
        }

        // Try error.data directly
        if (error.data) {
            const dataMessage = extractMessageFromData(error.data);
            if (dataMessage) return ensureUserFriendly(dataMessage);
        }

        // Try error.message
        if (error.message) {
            return ensureUserFriendly(extractMessageFromString(error.message));
        }

        // Fallback — prefer generic unexpected over "transaction would fail"
        // so wallet/connect/auth errors are not mislabeled as tx failures.
        return GENERIC_UNEXPECTED;
    } catch (e) {
        // If any error occurs during error parsing, return a safe generic message
        // This prevents technical errors from being shown to users
        console.error('Error parsing error message:', e);
        return GENERIC_UNEXPECTED;
    }
}

/**
 * Compose a user-facing error with an optional context prefix.
 * Avoids stacking generic "try again" lines onto the prefix.
 * @param {string} prefix - Short human context, e.g. "Could not connect to MetaMask"
 * @param {Error|Object|string} [error]
 * @returns {string}
 */
export function withCleanError(prefix, error) {
    const clean = getCleanErrorMessage(error);
    const base = stripSentenceDashes((prefix || '').replace(/[.!?:]\s*$/, '').trim());
    if (!base) return stripSentenceDashes(clean);
    if (clean.toLowerCase().startsWith(base.toLowerCase())) {
        return stripSentenceDashes(clean);
    }
    const isGeneric =
        clean === GENERIC_UNEXPECTED ||
        clean === GENERIC_TX_FAILURE ||
        clean === UNKNOWN_ERROR ||
        clean === WALLET_ERROR;
    // Generic detail adds nothing; keep the concrete prefix alone.
    if (isGeneric) {
        return `${base}.`;
    }
    // Specific detail is clearer on its own than "Prefix. Detail."
    return stripSentenceDashes(clean);
}

/**
 * Extracts a clean message from a string that may contain stack traces and hex data
 * @param {string} message - The error message string
 * @returns {string} - Clean error message
 */
function extractMessageFromString(message) {
    if (!message || typeof message !== 'string') {
        return UNKNOWN_ERROR;
    }

    // Check for JavaScript runtime errors first (before processing)
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('is not a function') ||
        lowerMessage.includes('cannot read property') ||
        lowerMessage.includes('cannot read properties') ||
        lowerMessage.includes('undefined is not an object') ||
        lowerMessage.includes('null is not an object') ||
        lowerMessage.includes('typeerror') ||
        lowerMessage.includes('referenceerror')) {
        return GENERIC_UNEXPECTED;
    }

    if (isInsufficientFundsError(message)) {
        return INSUFFICIENT_GAS_FUNDS;
    }

    // ethers CALL_EXCEPTION / missing revert dumps
    if (
        lowerMessage.includes('missing revert data') ||
        lowerMessage.includes('call_exception') ||
        lowerMessage.includes('unpredictable_gas_limit') ||
        /action\s*=\s*["']?estimateGas/i.test(message)
    ) {
        const revertMatch = message.match(/execution reverted(?:\s+with reason)?[:\s]+(.+?)(?:\s*(?:\(|code|version)|$)/i);
        if (revertMatch && revertMatch[1] && !looksTechnical(revertMatch[1])) {
            return formatErrorMessage(revertMatch[1].trim());
        }
        return GENERIC_TX_FAILURE;
    }

    // MetaMask style RPC endpoint HTTP client errors
    if (lowerMessage.includes('rpc endpoint returned http client error')) {
        return 'Your wallet RPC returned an error. Check that the RPC URL in your wallet matches the selected network, then try again.';
    }

    // Common wallet / JSON-RPC connection failures
    if (lowerMessage.includes('internal json-rpc') ||
        lowerMessage.includes('json-rpc error') ||
        lowerMessage.includes('json rpc error')) {
        return WALLET_ERROR;
    }
    if (lowerMessage.includes('already processing eth_requestaccounts') ||
        lowerMessage.includes('request already pending')) {
        return WALLET_REQUEST_PENDING;
    }
    if (lowerMessage.includes('underlying network changed') ||
        lowerMessage.includes('network changed')) {
        return WALLET_NETWORK_CHANGED;
    }
    if (lowerMessage.includes('unsupported chain') ||
        lowerMessage.includes('unrecognized chain') ||
        lowerMessage.includes('chain disconnected')) {
        return NETWORK_UNAVAILABLE;
    }

    // Firebase / auth service errors
    if (lowerMessage.includes('permission-denied') ||
        lowerMessage.includes('missing or insufficient permissions')) {
        return PERMISSION_DENIED;
    }
    if (lowerMessage.includes('auth/') || lowerMessage.includes('firebase')) {
        if (lowerMessage.includes('unavailable') || lowerMessage.includes('network-request-failed')) {
            return SERVICE_UNAVAILABLE;
        }
        return GENERIC_UNEXPECTED;
    }

    // Generic RPC/HTTP errors
    if (lowerMessage.includes('http request failed') ||
        lowerMessage.includes('fetch failed') ||
        lowerMessage.includes('failed to fetch') ||
        lowerMessage.includes('rpc connection error') ||
        lowerMessage.includes('rpc error') ||
        (lowerMessage.includes('failed to check gas balance') && (lowerMessage.includes('http') || lowerMessage.includes('rpc') || lowerMessage.includes('network')))) {
        // Keep "RPC connection error" phrase for callers that branch on it.
        return `RPC connection error: ${NETWORK_UNREACHABLE}`;
    }

    // Remove stack traces (lines starting with "    at" or containing file paths)
    // Also remove lines with technical metadata
    let cleanMessage = message.split('\n')
        .filter(line => {
            const trimmed = line.trim();
            // Filter out stack trace lines
            if (trimmed.startsWith('at ') || 
                trimmed.startsWith('Error:') && trimmed.includes('at ') ||
                trimmed.includes('chrome-extension://') ||
                trimmed.includes('common-') && trimmed.includes('.js') ||
                trimmed.includes('node_modules/') ||
                trimmed.includes('webpack://')) {
                return false;
            }
            // Filter out very long hex strings (likely error data)
            if (trimmed.length > 100 && /^0x[a-fA-F0-9]+$/.test(trimmed)) {
                return false;
            }
            // Filter out lines that are just technical metadata
            if (trimmed.match(/^(from|to|data|arguments|details|version|code|estimate gas):/i) && trimmed.includes('0x')) {
                return false;
            }
            return true;
        })
        .join(' ')
        .trim();

    // Strip ethers-style parenthetical dumps: (action="estimateGas", data=null, ..., code=CALL_EXCEPTION, )
    cleanMessage = cleanMessage.replace(
        /\(\s*(?:action|data|reason|transaction|invocation|revert|code)\s*=[^)]*\)/gi,
        ''
    ).trim();

    // Extract "execution reverted: <message>" or "execution reverted with reason: <message>" pattern
    // This handles both ethers.js and viem error formats
    // First, try to extract the core error message before any technical details
    const executionRevertedMatch = cleanMessage.match(/execution reverted(?:\s+with reason)?[:\s]+(.+?)(?:\s*(?:\(|code|version|available|required|token|address|estimate|gas|arguments|details|software|from|to|data|viem|ethers|suggestion)|$)/i);
    if (executionRevertedMatch && executionRevertedMatch[1]) {
        let revertMessage = executionRevertedMatch[1].trim();
        
        // Handle ERC20 errors - extract the error message
        // Match "ERC20: <error>" pattern, stopping before technical metadata
        const erc20Match = revertMessage.match(/ERC20[:\s]+([^\.]+?)(?:\.|$|\s*(?:\(|code|version|available|required|token|address|estimate|gas|arguments|details|software|from|to|data|viem|ethers|suggestion))/i);
        if (erc20Match && erc20Match[1]) {
            const erc20Error = erc20Match[1].trim();
            // Map common ERC20 errors to user-friendly messages
            const erc20ErrorLower = erc20Error.toLowerCase();
            if (erc20ErrorLower.includes('transfer amount exceeds balance')) {
                return INSUFFICIENT_TOKEN;
            }
            if (erc20ErrorLower.includes('transfer amount exceeds allowance')) {
                return INSUFFICIENT_ALLOWANCE;
            }
            if (erc20ErrorLower.includes('insufficient balance')) {
                return INSUFFICIENT_TOKEN;
            }
            if (erc20ErrorLower.includes('insufficient allowance')) {
                return INSUFFICIENT_ALLOWANCE;
            }
            // Return formatted ERC20 error if no specific mapping
            return formatErrorMessage(erc20Error);
        }
        
        // Handle TIP20 token errors - extract just the error type (e.g., "InsufficientBalance")
        const tip20Match = revertMessage.match(/TIP20\s+token\s+error[:\s]+([A-Za-z]+)/i);
        if (tip20Match && tip20Match[1]) {
            // Return just the error type, e.g., "InsufficientBalance" becomes "Insufficient balance"
            const errorType = tip20Match[1];
            // Convert camelCase to readable format
            const readableError = errorType.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
            return formatErrorMessage(readableError);
        }
        
        // Remove technical details that might appear after the error message
        // Stop at keywords that indicate technical metadata
        revertMessage = revertMessage.split(/\s*(?:available|required|token|address|estimate|gas|arguments|details|software|version|from|to|data|viem|ethers)/i)[0].trim();
        
        // Remove trailing metadata and hex data
        revertMessage = revertMessage.replace(/0x[a-fA-F0-9]{40,}/g, '').trim();
        revertMessage = revertMessage.split(/[(\s]+code|version|UNPREDICTABLE/i)[0].trim();
        
        if (revertMessage && !looksTechnical(revertMessage)) {
            return formatErrorMessage(revertMessage);
        }
    }

    // Extract error code patterns and provide friendly messages
    if (cleanMessage.includes('UNPREDICTABLE_GAS_LIMIT') || cleanMessage.includes('CALL_EXCEPTION')) {
        // Try to extract the underlying revert reason
        const revertMatch = cleanMessage.match(/revert[ed]?[:\s]+(.+?)(?:\s|$|\(|code|version)/i);
        if (revertMatch && revertMatch[1] && !looksTechnical(revertMatch[1])) {
            return formatErrorMessage(revertMatch[1].trim());
        }
        return GENERIC_TX_FAILURE;
    }

    // Remove JSON-like structures and hex data more aggressively
    cleanMessage = cleanMessage.replace(/\{[^}]*"code"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"message"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"data"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"from"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"to"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"arguments"[^}]*\}/g, '');
    cleanMessage = cleanMessage.replace(/\{[^}]*"details"[^}]*\}/g, '');
    
    // Remove all hex strings (addresses, data, etc.) - be aggressive
    cleanMessage = cleanMessage.replace(/0x[a-fA-F0-9]{20,}/g, '');
    
    // Remove technical metadata patterns
    cleanMessage = cleanMessage.replace(/code=(?:UNPREDICTABLE_GAS_LIMIT|CALL_EXCEPTION)[^,)]*/gi, '');
    cleanMessage = cleanMessage.replace(/\b(?:action|data|reason|transaction|invocation|revert)\s*=\s*[^,)\s]*/gi, '');
    cleanMessage = cleanMessage.replace(/version=[^,)]*/gi, '');
    cleanMessage = cleanMessage.replace(/suggestion[:\s]+[^\s,)]+/gi, '');
    
    // Remove detailed error information (available amount, required amount, token address, etc.)
    cleanMessage = cleanMessage.replace(/\b(available|required)\s+(amount|balance)[:\s]+\d+/gi, '');
    cleanMessage = cleanMessage.replace(/\btoken\s+address[:\s]+0x[a-fA-F0-9]{40}/gi, '');
    cleanMessage = cleanMessage.replace(/\bestimate\s+gas\s+arguments[:\s]*\{[^}]*\}/gi, '');
    cleanMessage = cleanMessage.replace(/\bdetails[:\s]*\{[^}]*\}/gi, '');
    cleanMessage = cleanMessage.replace(/\bsoftware\s+version[:\s]+[^\s,)]+/gi, '');
    
    // Remove viem/ethers specific metadata
    cleanMessage = cleanMessage.replace(/\bfrom[:\s]+0x[a-fA-F0-9]{40}/gi, '');
    cleanMessage = cleanMessage.replace(/\bto[:\s]+0x[a-fA-F0-9]{40}/gi, '');
    cleanMessage = cleanMessage.replace(/\bdata[:\s]+0x[a-fA-F0-9]+/gi, '');
    cleanMessage = cleanMessage.replace(/\barguments[:\s]*\{[^}]*\}/gi, '');
    cleanMessage = cleanMessage.replace(/\bviem@[\d.]+/gi, '');
    cleanMessage = cleanMessage.replace(/\bethers@[\d.]+/gi, '');
    
    // Remove lines that are just technical metadata (if message was multi-line)
    cleanMessage = cleanMessage.split('\n')
        .filter(line => {
            const trimmed = line.trim();
            // Remove lines that are just metadata
            if (trimmed.match(/^(from|to|data|arguments|details|version|code|suggestion|estimate gas):/i)) {
                return false;
            }
            return true;
        })
        .join(' ')
        .trim();

    // Remove common error metadata (including full ethers dumps that start with action=)
    cleanMessage = cleanMessage.replace(/\([^)]*(?:code|action|data|reason|invocation|revert)\s*=[^)]*\)/gi, '');
    cleanMessage = cleanMessage.replace(/\(version[^)]*\)/gi, '');

    // Strip SDK wrapper prefixes that leave only technical residue
    cleanMessage = cleanMessage.replace(/^failed to (?:transfer|deposit|withdraw|claim|prepare)[:\s]+/i, '').trim();
    cleanMessage = cleanMessage.replace(/\bmissing revert data\b/gi, '').trim();

    // Clean up multiple spaces / leftover punctuation from stripping
    cleanMessage = cleanMessage.replace(/\s+/g, ' ').trim();
    cleanMessage = cleanMessage.replace(/^[:\-.,\s]+|[:\-.,\s]+$/g, '').trim();

    if (!cleanMessage || looksTechnical(cleanMessage)) {
        return GENERIC_TX_FAILURE;
    }

    // If message is still too long or contains technical details, try to extract the meaningful part
    if (cleanMessage.length > 200) {
        // Try to find the first sentence or meaningful phrase
        const sentences = cleanMessage.split(/[.!?]/);
        if (sentences[0] && sentences[0].length < 200 && !looksTechnical(sentences[0])) {
            cleanMessage = sentences[0].trim();
        } else {
            return GENERIC_TX_FAILURE;
        }
    }

    return formatErrorMessage(cleanMessage || GENERIC_TX_FAILURE);
}

/**
 * Extracts error message from error data (hex encoded)
 * @param {string} data - Error data (usually hex encoded)
 * @returns {string|null} - Extracted message or null
 */
function extractMessageFromData(data) {
    if (!data || typeof data !== 'string') {
        return null;
    }

    // If it's a hex string starting with 0x, try to decode common error selectors
    if (data.startsWith('0x')) {
        // Common error selectors (first 4 bytes)
        const errorSelectors = {
            '0x08c379a0': 'Error(string)', // Standard Solidity error
            '0x4e487b71': 'Panic(uint256)', // Solidity panic
        };

        const selector = data.substring(0, 10);
        if (errorSelectors[selector]) {
            // Try to extract the string message (after the selector)
            try {
                // Error(string) encoding: selector (4 bytes) + offset (32 bytes) + length (32 bytes) + string data
                if (selector === '0x08c379a0' && data.length > 138) {
                    // This is a simplified extraction - full ABI decoding would be more accurate
                    return 'Transaction failed.';
                }
            } catch (e) {
                // Ignore decoding errors
            }
        }
    }

    return null;
}

/**
 * Formats error messages to be more user-friendly
 * @param {string} message - Raw error message
 * @returns {string} - Formatted error message
 */
function formatErrorMessage(message) {
    if (!message) {
        return 'Transaction failed. Try again.';
    }

    // Ensure message is a string
    if (typeof message !== 'string') {
        try {
            message = String(message);
        } catch (e) {
            return 'Transaction failed. Try again.';
        }
    }

    // Never format technical dumps into user-facing text
    if (looksTechnical(message)) {
        return GENERIC_UNEXPECTED;
    }

    // Common error message mappings
    const errorMappings = {
        'self-transfer': 'Cannot transfer to yourself.',
        'self transfer': 'Cannot transfer to yourself.',
        'insufficient funds': INSUFFICIENT_GAS_FUNDS,
        'insufficient balance': 'Insufficient balance.',
        'insufficientbalance': 'Insufficient balance.',
        'transfer amount exceeds balance': INSUFFICIENT_TOKEN,
        'transfer amount exceeds allowance': INSUFFICIENT_ALLOWANCE,
        'erc20: transfer amount exceeds balance': INSUFFICIENT_TOKEN,
        'erc20: transfer amount exceeds allowance': INSUFFICIENT_ALLOWANCE,
        'user denied': TX_CANCELLED,
        'user rejected': TX_CANCELLED,
        'user rejected the request': TX_CANCELLED,
        'transaction underpriced': 'Gas price too low. Try again.',
        'nonce too low': 'Transaction nonce error. Try again.',
        'replacement transaction underpriced': 'A similar transaction is already pending with a higher gas price.',
        'already known': 'This transaction is already pending.',
        'execution reverted': GENERIC_TX_FAILURE,
        'missing revert data': GENERIC_TX_FAILURE,
        'call_exception': GENERIC_TX_FAILURE,
        'cannot estimate gas': GENERIC_TX_FAILURE,
        'internal json-rpc': WALLET_ERROR,
        'request already pending': WALLET_REQUEST_PENDING,
        'already processing': WALLET_REQUEST_PENDING,
        'underlying network changed': WALLET_NETWORK_CHANGED,
        'unsupported chain': NETWORK_UNAVAILABLE,
        'unrecognized chain': NETWORK_UNAVAILABLE,
        'permission-denied': PERMISSION_DENIED,
        'missing or insufficient permissions': PERMISSION_DENIED,
        'please check console': GENERIC_UNEXPECTED,
    };

    const lowerMessage = message.toLowerCase().trim();

    for (const [key, value] of Object.entries(errorMappings)) {
        if (lowerMessage === key || lowerMessage.includes(key)) {
            return value;
        }
    }

    let formatted = stripSentenceDashes(message.trim());
    if (formatted.length > 0) {
        formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
        if (!formatted.match(/[.!?]$/)) {
            formatted += '.';
        }
    }

    return ensureUserFriendly(formatted, GENERIC_UNEXPECTED);
}

/**
 * Sanitize a notification/toast message before showing it to users.
 * Passes through React nodes and non-error types unchanged. For error strings
 * that still contain technical dumps, replaces them with a clean message.
 * @param {*} message
 * @param {string} [type]
 * @returns {*}
 */
export function sanitizeNotificationMessage(message, type = 'info') {
    if (type !== 'error') {
        return message;
    }
    if (typeof message !== 'string') {
        return message;
    }
    const deDashed = stripSentenceDashes(message);
    if (!looksTechnical(deDashed)) {
        return deDashed;
    }
    return getCleanErrorMessage(deDashed);
}


